import React, { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Download, Shield, Scale, Fingerprint, Copyright, Loader2,
  FileText, BookOpen, Lock, Award, Layers, CheckCircle2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DocItem {
  title: string;
  desc: string;
  file: string;
}

const patenteDocs: DocItem[] = [
  {
    title: 'Memorial Descritivo e Reivindicações',
    desc: '8 invenções descritas com memorial técnico completo, reivindicações independentes e dependentes',
    file: 'NetUno_Memorial_Descritivo_Patente.pdf',
  },
  {
    title: 'Reivindicações Consolidadas',
    desc: '25 reivindicações (8 independentes + 17 dependentes) cobrindo motores, algoritmos e governança',
    file: 'NetUno_Reivindicacoes_Patente.pdf',
  },
  {
    title: 'Resumos Técnicos das Invenções',
    desc: '8 resumos técnicos (~200 palavras cada) formatados para publicação no INPI',
    file: 'NetUno_Resumo_Tecnico_Patente.pdf',
  },
];

const softwareDocs: DocItem[] = [
  {
    title: 'Relatório Técnico do Sistema',
    desc: 'Visão geral da plataforma NetUno, classificação SGPI, módulos e benefícios mensuráveis',
    file: 'NetUno_Relatorio_Tecnico_Sistema.pdf',
  },
  {
    title: 'Registro de Programas de Computador',
    desc: '9 formulários INPI — plataforma principal, motores de cálculo e módulos especializados',
    file: 'NetUno_Documentacao_Registro_Software.pdf',
  },
  {
    title: 'Documento de Arquitetura do Sistema',
    desc: 'Arquitetura conceitual e funcional, fluxos de dados, integridade e segregação de acesso',
    file: 'NetUno_Arquitetura_Sistema.pdf',
  },
];

const marcaDocs: DocItem[] = [
  {
    title: 'Petições de Registro de Marca',
    desc: '7 pedidos — NetUno, SGPI, slogan "Menos riscos, melhores decisões, maior previsibilidade" e domínios',
    file: 'NetUno_Registro_Marca.pdf',
  },
];

const estrategiaDocs: DocItem[] = [
  {
    title: 'Metodologia Proprietária SGPI',
    desc: 'Fundamentos teóricos, modelo de governança, padronização operacional e benefícios para investidores',
    file: 'NetUno_Metodologia_SGPI.pdf',
  },
  {
    title: 'Estratégia de Propriedade Intelectual',
    desc: 'Mapa completo: o que patentear, registrar como software e proteger como marca',
    file: 'NetUno_Estrategia_Propriedade_Intelectual.pdf',
  },
  {
    title: 'Checklist de Protocolo no INPI',
    desc: 'Passo a passo com dados do titular, taxas, prazos e cronograma de depósito',
    file: 'NetUno_Checklist_INPI.pdf',
  },
];

const DocRow: React.FC<{ doc: DocItem; loading: string | null; onOpen: (f: string) => void }> = ({ doc, loading, onOpen }) => (
  <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
    <div className="min-w-0 flex-1 mr-3">
      <p className="font-medium text-sm">{doc.title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{doc.desc}</p>
    </div>
    <Button
      size="sm"
      variant="outline"
      className="gap-2 flex-shrink-0"
      disabled={loading === doc.file}
      onClick={() => onOpen(doc.file)}
    >
      {loading === doc.file ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      Abrir
    </Button>
  </div>
);

export const EmpPropriedadeIntelectualTab: React.FC = () => {
  const [loadingFile, setLoadingFile] = useState<string | null>(null);

  const handleOpenSecureDoc = useCallback(async (filename: string) => {
    setLoadingFile(filename);
    const previewWindow = window.open('', '_blank');

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        previewWindow?.close();
        toast.error('Sessão inválida para abrir o documento');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ip-doc-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ filename }),
        cache: 'no-store',
      });

      if (!response.ok) {
        previewWindow?.close();
        const errorData = await response.json().catch(() => null);
        toast.error(errorData?.error || 'Falha ao obter documento');
        return;
      }

      const fileBlob = await response.blob();
      const pdfBlob = fileBlob.type === 'application/pdf'
        ? fileBlob
        : new Blob([await fileBlob.arrayBuffer()], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);

      if (previewWindow) {
        previewWindow.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank');
      }

      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
      previewWindow?.close();
      toast.error('Erro ao acessar documento protegido');
    } finally {
      setLoadingFile(null);
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-l-4 border-l-amber-500">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-amber-500/10">
                <Fingerprint className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Propriedade Intelectual — NetUno</CardTitle>
                <CardDescription>
                  SGPI — Sistema de Governança de Projetos de Infraestrutura · "Menos riscos, melhores decisões, maior previsibilidade"
                </CardDescription>
              </div>
            </div>
            <Badge variant="outline" className="gap-1.5 py-1.5 px-3 border-amber-500 text-amber-700">
              <Lock className="w-3.5 h-3.5" /> Confidencial
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Documentação completa para proteção da propriedade intelectual da plataforma NetUno,
            incluindo pedidos de patente, registro de software, marca e metodologia proprietária — padrão INPI.
          </p>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-2.5 rounded-lg border bg-muted/20 text-center">
              <p className="text-lg font-bold text-foreground">10</p>
              <p className="text-xs text-muted-foreground">Documentos</p>
            </div>
            <div className="p-2.5 rounded-lg border bg-muted/20 text-center">
              <p className="text-lg font-bold text-foreground">8</p>
              <p className="text-xs text-muted-foreground">Patentes</p>
            </div>
            <div className="p-2.5 rounded-lg border bg-muted/20 text-center">
              <p className="text-lg font-bold text-foreground">9</p>
              <p className="text-xs text-muted-foreground">Registros de Software</p>
            </div>
            <div className="p-2.5 rounded-lg border bg-muted/20 text-center">
              <p className="text-lg font-bold text-foreground">7</p>
              <p className="text-xs text-muted-foreground">Marcas</p>
            </div>
          </div>
          <Separator className="my-3" />
          <p className="text-xs text-muted-foreground">
            Todos os documentos possuem campos <span className="bg-yellow-50 border border-dashed border-yellow-300 px-1 text-xs italic text-muted-foreground">[A PREENCHER]</span> para
            dados do titular, inventores e CNPJ/CPF. Abra no navegador e use "Imprimir → Salvar como PDF" ou copie para Word.
          </p>
        </CardContent>
      </Card>

      {/* Patente */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Scale className="w-5 h-5 text-amber-600" /> Pedido de Patente
          </CardTitle>
          <CardDescription>3 documentos — Memorial descritivo, reivindicações e resumos técnicos</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {patenteDocs.map(doc => (
            <DocRow key={doc.file} doc={doc} loading={loadingFile} onOpen={handleOpenSecureDoc} />
          ))}
        </CardContent>
      </Card>

      {/* Software */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Copyright className="w-5 h-5 text-blue-600" /> Registro de Software e Arquitetura
          </CardTitle>
          <CardDescription>3 documentos — Relatório técnico, formulários INPI e arquitetura do sistema</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {softwareDocs.map(doc => (
            <DocRow key={doc.file} doc={doc} loading={loadingFile} onOpen={handleOpenSecureDoc} />
          ))}
        </CardContent>
      </Card>

      {/* Marca */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Award className="w-5 h-5 text-emerald-600" /> Registro de Marca
          </CardTitle>
          <CardDescription>1 documento — Petições consolidadas para proteção de marca</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {marcaDocs.map(doc => (
            <DocRow key={doc.file} doc={doc} loading={loadingFile} onOpen={handleOpenSecureDoc} />
          ))}
        </CardContent>
      </Card>

      {/* Estratégia */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Layers className="w-5 h-5 text-violet-600" /> Estratégia e Protocolo
          </CardTitle>
          <CardDescription>3 documentos — Metodologia SGPI, estratégia de PI e checklist INPI</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {estrategiaDocs.map(doc => (
            <DocRow key={doc.file} doc={doc} loading={loadingFile} onOpen={handleOpenSecureDoc} />
          ))}
        </CardContent>
      </Card>

      {/* Nota de uso */}
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-amber-700 text-sm">Uso e Confidencialidade</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Esta documentação é de uso exclusivo da empresa titular e seus representantes legais.
                Destina-se a processos de registro junto ao INPI, due diligence, auditorias de conformidade
                e apresentações a investidores e parceiros estratégicos. A reprodução ou compartilhamento
                não autorizado está sujeita às penalidades legais aplicáveis.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
